/**
 * useAppSession — resolves who is using the app at startup.
 *
 * Priority:
 *   1. DT OS token in memory → GET /auth/session → employee session
 *   2. No token + window.shopify available → GET /auth/bootstrap/status
 *      → if required: show BootstrapPage
 *      → if not required: show DtOsLogin
 *   3. No token + no Shopify context → show DtOsLogin
 *
 * Shopify is used only to check bootstrap status (one-time setup).
 * All authentication after bootstrap is handled exclusively by DT OS.
 */

import { createContext, useContext, useEffect, useState } from 'react'
import { ENV }                  from '../config/environment'
import { getShop }              from '../sdk/shop'

// ── Session Context ───────────────────────────────────────────────────────────
// AppShell provides the resolved session here; any component or hook in the
// tree can read it with useSession() without an extra /auth/session round-trip.

export const SessionContext = createContext<AppSession | null>(null)

/** Read the DT OS session from the nearest SessionContext provider. */
export function useSession(): AppSession | null {
  return useContext(SessionContext)
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type WorkflowMembership = {
  workflowId:           string
  role:                 'manager' | 'agent' | 'viewer'
  effectivePermissions: Record<string, unknown>
}

export type EmployeeSession = {
  type:           'employee'
  employeeId:     string
  displayName:    string
  tenantRole:     'admin' | 'member' | 'owner'
  mustChangePass: boolean
  memberships:    WorkflowMembership[]
  opsPermissions: Record<string, unknown> | null
}

export type AppSession = EmployeeSession

export type AppSessionState = {
  loading:           boolean
  session:           AppSession | null
  bootstrapRequired: boolean
  error:             'invalid_token' | 'network' | null
}

// ── DT OS token storage (sessionStorage — survives reload, cleared on tab close) ─

const SESSION_KEY = 'dt-os-token'

// In-memory mirror so we don't hit sessionStorage on every read within a session.
let _dtToken: string | null = typeof sessionStorage !== 'undefined'
  ? sessionStorage.getItem(SESSION_KEY)
  : null

export function storeDtOsToken(token: string): void {
  _dtToken = token
  sessionStorage.setItem(SESSION_KEY, token)
}

export function clearDtOsToken(): void {
  _dtToken = null
  sessionStorage.removeItem(SESSION_KEY)
}

export function hasDtOsToken(): boolean {
  return _dtToken !== null
}

// ── Internal: validate DT OS session token ────────────────────────────────────

async function fetchSession(bearerToken: string, shop: string): Promise<AppSession | null> {
  const url = new URL(`${ENV.API_BASE_URL}/auth/session`)
  url.searchParams.set('shop', shop)

  const res = await fetch(url.toString(), {
    method:  'GET',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${bearerToken}`,
    },
    signal: AbortSignal.timeout(8000),
  })

  if (!res.ok) return null
  return res.json() as Promise<AppSession>
}

// ── Internal: check bootstrap status (public endpoint, no auth required) ─────

async function fetchBootstrapStatus(
  shop: string,
): Promise<boolean> {
  const url = new URL(`${ENV.API_BASE_URL}/auth/bootstrap/status`)
  url.searchParams.set('shop', shop)

  try {
    const res = await fetch(url.toString(), {
      method: 'GET',
      signal: AbortSignal.timeout(8000),
    })
    if (!res.ok) return false
    const data = await res.json() as { required: boolean }
    return data.required === true
  } catch {
    return false
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAppSession(): AppSessionState {
  const [state, setState] = useState<AppSessionState>({
    loading:           true,
    session:           null,
    bootstrapRequired: false,
    error:             null,
  })

  useEffect(() => {
    let cancelled = false

    async function resolve() {
      const shop = getShop() ?? ''

      // ── DT OS token: verify existing session ──────────────────────────────
      if (_dtToken) {
        try {
          const session = await fetchSession(_dtToken, shop)
          if (cancelled) return

          if (session?.type === 'employee') {
            setState({ loading: false, session, bootstrapRequired: false, error: null })
          } else {
            _dtToken = null
            setState({ loading: false, session: null, bootstrapRequired: false, error: null })
          }
        } catch {
          if (!cancelled) setState({ loading: false, session: null, bootstrapRequired: false, error: 'network' })
        }
        return
      }

      // ── No token: check bootstrap status (public endpoint, no JWT needed) ──
      const required = await fetchBootstrapStatus(shop)
      if (cancelled) return

      setState({ loading: false, session: null, bootstrapRequired: required, error: null })
    }

    resolve()
    return () => { cancelled = true }
  }, [])

  return state
}
